import React, { useState, useEffect } from 'react';
import { Form, Button, Spinner, Card, Row, Col } from 'react-bootstrap';
import Select from 'react-select';
import { toast } from 'react-toastify';
import ResponseParametersForm from '../components/ResponseParametersForm';

const ResponseFormatAction = ({ 
    formData, 
    setFormData, 
    allTableList, 
    selectedRequest, 
    allPreviousSteps, 
    currentStepId 
}) => {
    const [responseMappings, setResponseMappings] = useState(formData?.responseMappings || []);
    const [responseParameters, setResponseParameters] = useState(formData?.responseParameters || []);
    const [availableSteps, setAvailableSteps] = useState([]);
    const [stepParameters, setStepParameters] = useState({});
    const [loading, setLoading] = useState(false);

    // Initialize available steps and their parameters
    useEffect(() => {
        if (allPreviousSteps && allPreviousSteps.length > 0) {
            const steps = allPreviousSteps.map(step => {
                const stepAction = step.action || step.actions?.[0] || {};
                return {
                    value: step.id,
                    label: `Step ${step.id} - ${stepAction.type || stepAction.id || 'Unknown'}`,
                    step: step
                };
            });
            setAvailableSteps(steps);

            // Extract parameters from each step (support both step.action and step.actions[0])
            const params = {};
            allPreviousSteps.forEach(step => {
                const stepAction = step.action || step.actions?.[0] || {};
                const stepResponseParams = stepAction?.config?.responseParameters;
                if (Array.isArray(stepResponseParams) && stepResponseParams.length > 0) {
                    params[step.id] = stepResponseParams;
                }
            });
            setStepParameters(params);
        } else {
            setAvailableSteps([]);
            setStepParameters({});
        }
    }, [allPreviousSteps]);

    // Utility shallow-deep compare using JSON.stringify for simplicity
    const isEqual = (a, b) => {
        try {
            return JSON.stringify(a ?? null) === JSON.stringify(b ?? null);
        } catch (e) {
            return a === b;
        }
    };

    // Initialize/sync from incoming formData only when fields actually change
    useEffect(() => {
        const incomingMappings = formData?.responseMappings || [];
        const incomingParams = formData?.responseParameters || [];
        if (!isEqual(incomingMappings, responseMappings)) {
            setResponseMappings(incomingMappings);
        }
        if (!isEqual(incomingParams, responseParameters)) {
            setResponseParameters(incomingParams);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formData?.responseMappings, formData?.responseParameters]);

    // Update formData when local state changes
    useEffect(() => {
        setFormData(prev => {
            const prevMappings = prev?.responseMappings || [];
            const prevParams = prev?.responseParameters || [];
            if (isEqual(prevMappings, responseMappings) && isEqual(prevParams, responseParameters)) {
                return prev; // avoid unnecessary parent updates that can cause loops
            }
            return {
                ...prev,
                responseMappings,
                responseParameters
            };
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [responseMappings, responseParameters]);

    const handleAddMapping = () => {
        const newMapping = {
            id: Date.now(),
            responseField: '',
            sourceStep: '',
            sourceParameter: '',
            sourceAttribute: '',
            transformation: 'direct', // direct, concat, format, etc.
            defaultValue: ''
        };
        setResponseMappings([...responseMappings, newMapping]);
    };

    const handleUpdateMapping = (id, field, value) => {
        setResponseMappings(prev => 
            prev.map(mapping => 
                mapping.id === id ? { ...mapping, [field]: value } : mapping
            )
        );
    };

    const handleRemoveMapping = (id) => {
        setResponseMappings(prev => prev.filter(mapping => mapping.id !== id));
    };

    const getStepParameterOptions = (stepId) => {
        if (!stepId || !stepParameters[stepId]) return [];
        return stepParameters[stepId].map(param => ({
            value: param.name || param.id,
            label: `${param.name || param.id} (${param.type || 'unknown'})`,
            param: param
        }));
    };

    const getParameterAttributeOptions = (stepId, parameterName) => {
        if (!stepId || !parameterName || !stepParameters[stepId]) return [];
        
        const parameter = stepParameters[stepId].find(param => 
            param.name === parameterName || param.id === parameterName
        );
        
        if (parameter && parameter.config && parameter.config.attributes) {
            return parameter.config.attributes.map(attr => ({
                value: attr.name || attr.id,
                label: `${attr.name || attr.id} (${attr.type || 'unknown'})`
            }));
        }
        
        return [];
    };

    const renderMappingForm = (mapping) => {
        const stepOptions = availableSteps;
        const parameterOptions = getStepParameterOptions(mapping.sourceStep);
        const attributeOptions = getParameterAttributeOptions(mapping.sourceStep, mapping.sourceParameter);

        return (
            <Card key={mapping.id} className="mb-3">
                <Card.Body>
                    <Row>
                        <Col md={3}>
                            <Form.Group>
                                <Form.Label>Response Field</Form.Label>
                                <Form.Control
                                    type="text"
                                    placeholder="e.g., customerName, orderTotal"
                                    value={mapping.responseField}
                                    onChange={(e) => handleUpdateMapping(mapping.id, 'responseField', e.target.value)}
                                />
                            </Form.Group>
                        </Col>
                        <Col md={3}>
                            <Form.Group>
                                <Form.Label>Source Step</Form.Label>
                                <Select
                                    options={stepOptions}
                                    value={stepOptions.find(option => option.value === mapping.sourceStep)}
                                    onChange={(option) => {
                                        handleUpdateMapping(mapping.id, 'sourceStep', option?.value || '');
                                        handleUpdateMapping(mapping.id, 'sourceParameter', '');
                                        handleUpdateMapping(mapping.id, 'sourceAttribute', '');
                                    }}
                                    placeholder="Select step..."
                                />
                            </Form.Group>
                        </Col>
                        <Col md={3}>
                            <Form.Group>
                                <Form.Label>Source Parameter</Form.Label>
                                <Select
                                    options={parameterOptions}
                                    value={parameterOptions.find(option => option.value === mapping.sourceParameter)}
                                    onChange={(option) => {
                                        handleUpdateMapping(mapping.id, 'sourceParameter', option?.value || '');
                                        handleUpdateMapping(mapping.id, 'sourceAttribute', '');
                                    }}
                                    placeholder="Select parameter..."
                                    isDisabled={!mapping.sourceStep}
                                />
                            </Form.Group>
                        </Col>
                        <Col md={3}>
                            <Form.Group>
                                <Form.Label>Source Attribute</Form.Label>
                                <Select
                                    options={attributeOptions}
                                    value={attributeOptions.find(option => option.value === mapping.sourceAttribute)}
                                    onChange={(option) => handleUpdateMapping(mapping.id, 'sourceAttribute', option?.value || '')}
                                    placeholder="Select attribute..."
                                    isDisabled={!mapping.sourceParameter}
                                    isClearable
                                />
                            </Form.Group>
                        </Col>
                    </Row>
                    <Row className="mt-2">
                        <Col md={3}>
                            <Form.Group>
                                <Form.Label>Transformation</Form.Label>
                                <Select
                                    options={[
                                        { value: 'direct', label: 'Direct Copy' },
                                        { value: 'concat', label: 'Concatenate' },
                                        { value: 'format', label: 'Format' },
                                        { value: 'calculate', label: 'Calculate' },
                                        { value: 'conditional', label: 'Conditional' }
                                    ]}
                                    value={{ value: mapping.transformation, label: mapping.transformation === 'direct' ? 'Direct Copy' : 
                                           mapping.transformation === 'concat' ? 'Concatenate' : 
                                           mapping.transformation === 'format' ? 'Format' : 
                                           mapping.transformation === 'calculate' ? 'Calculate' : 
                                           mapping.transformation === 'conditional' ? 'Conditional' : 'Direct Copy' }}
                                    onChange={(option) => handleUpdateMapping(mapping.id, 'transformation', option?.value || 'direct')}
                                />
                            </Form.Group>
                        </Col>
                        <Col md={6}>
                            <Form.Group>
                                <Form.Label>Default Value</Form.Label>
                                <Form.Control
                                    type="text"
                                    placeholder="Default value if source is empty"
                                    value={mapping.defaultValue}
                                    onChange={(e) => handleUpdateMapping(mapping.id, 'defaultValue', e.target.value)}
                                />
                            </Form.Group>
                        </Col>
                        <Col md={3} className="d-flex align-items-end">
                            <Button
                                variant="outline-danger"
                                size="sm"
                                onClick={() => handleRemoveMapping(mapping.id)}
                            >
                                Remove
                            </Button>
                        </Col>
                    </Row>
                </Card.Body>
            </Card>
        );
    };

    return (
        <div>
            <div className="mb-4">
                <h5>Response Format Configuration</h5>
                <p className="text-muted">
                    Map data from previous workflow steps to create a structured final response.
                </p>
            </div>

            <div className="mb-4">
                <h6>Response Mappings</h6>
                <p className="text-muted">
                    Define how data from previous steps should be mapped to the final response.
                </p>
                
                {responseMappings.length === 0 ? (
                    <div className="text-center py-4 text-muted">
                        <p>No response mappings configured yet.</p>
                        <p>Click "Add Mapping" to start building your response format.</p>
                    </div>
                ) : (
                    responseMappings.map(mapping => renderMappingForm(mapping))
                )}
                
                <Button
                    variant="outline-primary"
                    onClick={handleAddMapping}
                    className="mt-2"
                >
                    <i className="fas fa-plus me-2"></i>
                    Add Mapping
                </Button>
            </div>

            <div className="mb-4">
                <h6>Response Parameters</h6>
                <p className="text-muted">
                    Define the structure and types of the final response parameters.
                </p>
                <ResponseParametersForm
                    responseParameters={responseParameters}
                    setResponseParameters={setResponseParameters}
                />
            </div>

            {responseMappings.length > 0 && (
                <div className="mb-4">
                    <h6>Response Preview</h6>
                    <div className="bg-light p-3 rounded">
                        <pre className="mb-0">
                            <code>
                                {JSON.stringify(
                                    responseMappings.reduce((acc, mapping) => {
                                        if (mapping.responseField) {
                                            acc[mapping.responseField] = `{step_${mapping.sourceStep}.${mapping.sourceParameter}${mapping.sourceAttribute ? '.' + mapping.sourceAttribute : ''}}`;
                                        }
                                        return acc;
                                    }, {}),
                                    null,
                                    2
                                )}
                            </code>
                        </pre>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ResponseFormatAction;
